import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import { Heading, Text, Grid, useTheme } from '@simplywallst/ui-core';

import { useFlag } from '@/features/unleash';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';

import { ImpressionTracking } from './ImpressionTracking';
import { AuthorImage, Item } from './styled';
import type { Company, FeedItem } from './types';

interface Props {
  company: Company;
  feedItem: FeedItem;
  index: number;
  onIntersection?: (id: string) => void;
}

const NarrativeFeedCard = ({
  company,
  feedItem,
  index,
  onIntersection,
}: Props) => {
  const { companyId } = company;
  const {
    activityId,
    authorImageUrl,
    dateFormatted,
    id,
    subType,
    text,
    title,
    type,
    url,
  } = feedItem;
  const theme = useTheme();

  const fallbackImageUrl =
    'https://media.simplywall.st/news/1695690646346-fallback-author-image.png';
  const imageUrl =
    typeof authorImageUrl !== 'undefined' &&
    /^https:\/\/(images|media)\.simplywall\.st/.test(authorImageUrl)
      ? authorImageUrl
      : fallbackImageUrl;

  const cachedAuthorImageUrl = useCloudflareImage({
    src: imageUrl,
    width: 60,
    height: 60,
    fit: 'cover',
  });
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'feed',
  });

  const trackClick = useCallback(() => {
    const eventId = id;
    trackEvent({
      action: 'click',
      modifier: 'card-item',
      index: index,
      eventType: type,
      eventSubtype: subType,
      eventId,
      activityId,
      companyId,
    });
  }, [trackEvent, index, type, subType, id, activityId, companyId]);

  const companyCanonicalUrl = company.canonicalUrl.split('/');

  const slug = [
    '',
    'community',
    'narratives',
    ...companyCanonicalUrl.slice(2),
    url.split('/').pop(),
  ].join('/');

  return (
    <Track>
      <RouterLink noUnderline to={slug} onClick={() => trackClick()}>
        <ImpressionTracking id={activityId} onIntersection={onIntersection}>
          <Item
            alignItems="start"
            as="article"
            data-cy-id="feed-card-item"
            gap={`${theme.x1_5}px`}
            hoverBackground="phantom"
            templateColumns="min-content auto"
          >
            <AuthorImage src={cachedAuthorImageUrl} />
            <Grid rowGap={`${theme.x0_5}px`}>
              {title ? (
                <Heading
                  as="h3"
                  typography="xSmall"
                  color="white"
                  lineClamp={3}
                >
                  {title}
                </Heading>
              ) : null}{' '}
              <Text as="span" typography="tiny" color="whiteRGBA" opacity={0.5}>
                {dateFormatted}
              </Text>
              <Text
                as="span"
                typography="tiny"
                color="whiteRGBA"
                opacity={0.5}
                lineClamp={2}
              >
                {text}
              </Text>
            </Grid>
          </Item>
        </ImpressionTracking>
      </RouterLink>
    </Track>
  );
};

export const FlaggedNarrativeFeedCard = (props: Props) => {
  const featureFlag = useFlag({ name: 'mono-narrative-feed' });
  const isFeatureEnabled = featureFlag.flagsReady && featureFlag.enabled;
  if (!isFeatureEnabled) return null;
  return <NarrativeFeedCard {...props} />;
};
